import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormSectionStateService,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionReadonlyBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import {
  AppMetaData,
  AppMetaDataItemNames,
  FlightSchoolBrandingEntity,
  FlightSchoolEntity,
  FlightSchoolRegistrationEmailDomainEntity,
  SubdomainSetupStatusAllItems,
  SubdomainTypeAllItems
} from '../../../../meta-data/app-meta-data.service';
import { SchoolFeatures } from '../../models/school-features.model';
import { SchoolRepositoryService } from '../../services/school-repository.service';

@DwComponent({
  key: 'flight-school-registration-domains-form-section',
  name: 'Flight School Domain Setup - Form Section',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.FlightSchool,
  isGlobal: false
})
@Component({
  selector: 'app-flight-school-registration-domains-form-section',
  templateUrl: './flight-school-registration-domains-form-section.component.html',
  styleUrls: ['./flight-school-registration-domains-form-section.component.scss']
})
export class FlightSchoolRegistrationDomainsFormSectionComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  readonly subdomainType = SubdomainTypeAllItems.SubdomainAviatorProCom;
  readonly fullDomainType = SubdomainTypeAllItems.FullDomain;

  domains: string[] = [];
  displayDomains: string[] = [];
  sectionReadOnly = false;
  domainSetupStarted = false;
  domainSetupComplete = false;
  showDomainDetails = false;
  setupTypeDisplay = '';
  domainDisplay = '';
  studentRegistrationLink = '';
  instructorRegistrationLink = '';
  allowInstructorRegistrationRoleSelection = false;
  studentLinkCopied = false;
  instructorLinkCopied = false;
  branding: FlightSchoolBrandingEntity;
  schoolFeatures: SchoolFeatures;

  private readonly domainControlName = 'FlightSchoolRegistrationEmailDomain';
  private readonly brandingControlName = 'FlightSchoolBranding';
  private readonly destroy$ = new Subject<void>();
  private readonly formApi: DwMetaDataFormApi;
  private domainControl: AbstractControl;
  private brandingControl: AbstractControl;
  private restrictDomainsControl: AbstractControl;
  private instructorRoleSelectionControl: AbstractControl;
  private hydrated = false;
  private syncing = false;

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwFormSectionStateService: DwMetaDataFormSectionStateService,
    dwFormStateService: DwMetaDataFormStateService,
    private schoolRepositoryService: SchoolRepositoryService
  ) {
    super(dwExpressionService, dwFormSectionStateService, dwFormStateService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.sectionReadOnly = !!this.isSectionReadOnly();

    this.runAfterFormDataLoaded(() => {
      this.setupFormControls();
      this.syncFromForm(true);
      this.loadSchoolFeatures();
    });
    this.formApi.getFormDataChanges()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.syncFromForm(false));
  }

  private loadSchoolFeatures(): void {
    const flightSchoolId = this.formApi.getFormData<FlightSchoolEntity>()?.FlightSchoolId;

    if (flightSchoolId == null) {
      return;
    }

    this.schoolRepositoryService.getSchoolFeaturesForSchool(flightSchoolId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(features => {
        this.schoolFeatures = features;
        this.buildViewModel();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    this.sectionReadOnly = !!readOnly;
  }

  startDomainSetup(): void {
    if (this.sectionReadOnly) {
      return;
    }

    this.ensureBranding();
    this.branding.SubdomainTypeId = SubdomainTypeAllItems.SubdomainAviatorProCom;
    this.branding.SubdomainSetupStatusId = SubdomainSetupStatusAllItems.New;
    this.patchBranding();
    this.buildViewModel();
  }

  brandingChanged(): void {
    if (!this.branding || this.sectionReadOnly || this.domainSetupComplete) {
      return;
    }

    this.branding.DomainName = (this.branding.DomainName || '').trim().toLowerCase();
    this.patchBranding();
    this.buildViewModel();
  }

  autoJoinChanged(enabled: boolean): void {
    if (!this.branding || this.sectionReadOnly) {
      return;
    }

    this.branding.DomainAutoJoinSchool = enabled;
    this.patchBranding();
    this.buildViewModel();
  }

  instructorRoleSelectionChanged(enabled: boolean): void {
    if (this.sectionReadOnly || !this.domainSetupComplete || !this.instructorRoleSelectionControl) {
      return;
    }

    this.allowInstructorRegistrationRoleSelection = enabled;
    this.instructorRoleSelectionControl.setValue(enabled);
    this.formApi.patchContextData('data', { AllowInstructorRegistrationRoleSelection: enabled });
    this.instructorRoleSelectionControl.markAsTouched();
  }

  domainsChanged(domains: string[]): void {
    if (this.syncing || !this.domainControl) {
      return;
    }

    const selectedDomains = this.normalizeDomains(domains || []);
    const entities = [...((this.domainControl.value || []) as FlightSchoolRegistrationEmailDomainEntity[])];
    const school = this.formApi.getFormData<FlightSchoolEntity>();

    entities.forEach(entity => entity.Active = false);
    selectedDomains.forEach(domain => {
      let entity = entities.find(item => this.normalizeDomain(item.EmailDomain) === domain);

      if (!entity) {
        entity = AppMetaData.FlightSchoolRegistrationEmailDomain.CreateEntity();
        entities.push(entity);
      }

      entity.FlightSchoolId = entity.FlightSchoolId || school?.FlightSchoolId;
      entity.EmailDomain = domain;
      entity.Active = true;
    });

    const patchData: Partial<FlightSchoolEntity> = {
      FlightSchoolRegistrationEmailDomain: entities,
      RestrictRegistrationEmailDomains: selectedDomains.length > 0
    };
    this.domainControl.setValue(entities);
    this.restrictDomainsControl?.setValue(selectedDomains.length > 0);
    this.formApi.patchContextData('data', patchData);
    this.domainControl.markAsTouched();
    this.domains = selectedDomains;
    this.displayDomains = selectedDomains;
  }

  copyRegistrationLink(link: string, linkType: 'student' | 'instructor'): void {
    if (!link || !navigator.clipboard) {
      return;
    }

    navigator.clipboard.writeText(link).then(() => {
      if (linkType === 'student') {
        this.studentLinkCopied = true;
        setTimeout(() => this.studentLinkCopied = false, 2000);
        return;
      }

      this.instructorLinkCopied = true;
      setTimeout(() => this.instructorLinkCopied = false, 2000);
    });
  }

  trackDomain(index: number, domain: string): string {
    return domain;
  }

  private syncFromForm(hydrate: boolean): void {
    const school = this.formApi.getFormData<FlightSchoolEntity>();
    // The root form control is the source of truth while View/Edit is active.
    // formApi may still contain the last persisted relationship until Save completes.
    this.branding = this.brandingControl?.value || school?.FlightSchoolBranding;
    this.allowInstructorRegistrationRoleSelection = !!(this.instructorRoleSelectionControl?.value
      ?? school?.AllowInstructorRegistrationRoleSelection);
    const entities = (this.domainControl?.value
      || school?.FlightSchoolRegistrationEmailDomain
      || []) as FlightSchoolRegistrationEmailDomainEntity[];
    const activeDomains = this.normalizeDomains(entities.filter(entity => entity.Active !== false).map(entity => entity.EmailDomain));
    this.displayDomains = activeDomains;

    if (hydrate && !this.hydrated) {
      this.syncing = true;
      this.domains = activeDomains;
      this.hydrated = true;
      this.syncing = false;
    }

    this.buildViewModel();
  }

  private setupFormControls(): void {
    const school = this.formApi.getFormData<FlightSchoolEntity>();
    this.domainControl = this.getOrCreateFormControl(this.domainControlName);
    this.brandingControl = this.getOrCreateFormControl(this.brandingControlName);
    this.restrictDomainsControl = this.getOrCreateFormControl('RestrictRegistrationEmailDomains');
    this.instructorRoleSelectionControl = this.getOrCreateFormControl('AllowInstructorRegistrationRoleSelection');

    if (this.domainControl.value == null) {
      this.domainControl.setValue(school?.FlightSchoolRegistrationEmailDomain || [], { emitEvent: false });
    }

    if (this.brandingControl.value == null) {
      this.brandingControl.setValue(school?.FlightSchoolBranding || null, { emitEvent: false });
    }

    if (this.restrictDomainsControl.value == null) {
      this.restrictDomainsControl.setValue(!!school?.RestrictRegistrationEmailDomains, { emitEvent: false });
    }

    if (this.instructorRoleSelectionControl.value == null) {
      this.instructorRoleSelectionControl.setValue(!!school?.AllowInstructorRegistrationRoleSelection, { emitEvent: false });
    }
  }

  private buildViewModel(): void {
    const typeId = this.branding?.SubdomainTypeId || SubdomainTypeAllItems.NotSetup;
    const statusId = this.branding?.SubdomainSetupStatusId;
    this.domainSetupStarted = typeId !== SubdomainTypeAllItems.NotSetup;
    this.domainSetupComplete = statusId === SubdomainSetupStatusAllItems.Completed;
    this.showDomainDetails = this.domainSetupStarted
      && (statusId === SubdomainSetupStatusAllItems.New || this.domainSetupComplete);
    this.setupTypeDisplay = typeId === SubdomainTypeAllItems.FullDomain
      ? 'Full domain'
      : 'Subdomain – AviatorPro.com';
    this.domainDisplay = this.getDomainDisplay(typeId);
    this.studentRegistrationLink = this.schoolFeatures?.FlightSchoolRegistrationUrl || '';
    this.instructorRegistrationLink = this.schoolFeatures?.FlightSchoolInstructorRegistrationUrl || '';
  }

  private getDomainDisplay(typeId: number): string {
    const domainName = (this.branding?.DomainName || '').trim();

    if (!domainName) {
      return '';
    }

    if (typeId === SubdomainTypeAllItems.FullDomain) {
      return domainName;
    }

    const configuredUrl = this.domainSetupComplete ? this.schoolFeatures?.FlightSchoolUrl : '';
    if (configuredUrl) {
      return configuredUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    }

    return `${domainName}.aviatorpro.com`;
  }

  private ensureBranding(): void {
    if (this.branding) {
      return;
    }

    const school = this.formApi.getFormData<FlightSchoolEntity>();
    this.branding = AppMetaData.FlightSchoolBranding.CreateEntity();
    this.branding.FlightSchoolBrandingId = school?.FlightSchoolBrandingId;
  }

  private patchBranding(): void {
    const patchData: Partial<FlightSchoolEntity> = { FlightSchoolBranding: this.branding };
    this.brandingControl?.setValue(this.branding);
    this.formApi.patchContextData('data', patchData);
    this.brandingControl?.markAsTouched();
  }

  private normalizeDomains(domains: string[]): string[] {
    return domains
      .map(domain => this.normalizeDomain(domain))
      .filter((domain, index, values) => !!domain && values.indexOf(domain) === index);
  }

  private normalizeDomain(domain: string): string {
    return (domain || '').trim().replace(/^@/, '').toLowerCase();
  }
}
